import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Spinner,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  TabsHeader,
  Tab,
  TabsBody,
  TabPanel,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from '@material-tailwind/react';
import {
  PencilIcon,
  ArrowPathIcon,
  DocumentPlusIcon,
  UserGroupIcon,
  UserIcon,
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  GlobeAltIcon,
  ChevronDownIcon,
  ShareIcon,
  NoSymbolIcon,
  EyeIcon,
  ListBulletIcon,
  CheckBadgeIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

import { collection, query, where, getDocs } from "firebase/firestore";
import db from "../../firebase";
// import { DefaultPagination } from "../../components/Pagination";
import { getAllDrafts } from '../../fb-functions';
import { listDrafts, getMyReportsAnalytics, archiveDraft, deactivateDraftShares } from '../../services/reportApi';
import ShareDialog from '../../components/ShareDialog';
import { UserAuth } from '../../context/AuthContext';
import { friendlyTimeFormatDt } from '../../utils';

// Format active engagement time
function formatActiveTime(seconds) {
  if (!seconds || seconds === 0) return null;
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

// Get engagement level based on active time
function getEngagementColor(seconds) {
  if (!seconds || seconds < 60) return 'text-gray-400';
  if (seconds < 180) return 'text-blue-500';
  return 'text-green-600';
}

// A share is live only if active AND not past its expiry date
function isShareLive(share) {
  return share.isActive && !(share.expiresAt && new Date(share.expiresAt) < new Date());
}

const STATUS_COLORS = {
  draft: 'gray',
  pending_review: 'amber',
  finalizing: 'blue',
  finalized: 'green',
  error: 'red',
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  finalizing: 'Processing',
  finalized: 'Published',
  error: 'Error',
};

function AllDraftsGrid({ drafts, isAdmin, onEdit, onArchive, archivingId, onDeactivateLinks, deactivatingId, expandedData, expandLoading, toggleExpandDraft, setShareDialogReport, user, delegateFor }) {
  const [selectedDraft, setSelectedDraft] = useState(null);

  const handleRowClick = useCallback((draft) => {
    const draftId = draft.draftId || draft.id;
    setSelectedDraft(draft);
    toggleExpandDraft(draftId);
  }, [toggleExpandDraft]);

  const colDefs = useMemo(() => [
    {
      field: 'reportType',
      headerName: 'Type',
      width: 130,
      filter: 'agTextColumnFilter',
      valueGetter: (p) => p.data.reportType === 'marketing_update' ? 'Marketing' : 'Valuation',
      cellRenderer: (p) => {
        const isMarketing = p.data.reportType === 'marketing_update';
        return (
          <Chip
            size="sm"
            value={isMarketing ? 'Marketing' : 'Valuation'}
            color={isMarketing ? 'purple' : 'blue'}
            variant="ghost"
            className="text-xs py-0.5 px-2"
          />
        );
      },
    },
    {
      field: 'aircraftId',
      headerName: 'Reg',
      filter: 'agTextColumnFilter',
      width: 100,
      valueGetter: (p) => p.data.aircraftId || p.data.subjectAircraft?.registration || '-',
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      field: 'customerName',
      headerName: 'Customer',
      filter: 'agTextColumnFilter',
      flex: 1,
      minWidth: 140,
      valueGetter: (p) => p.data.customerName || '-',
    },
    {
      headerName: 'Aircraft',
      filter: 'agTextColumnFilter',
      flex: 1.2,
      minWidth: 180,
      valueGetter: (p) => {
        const d = p.data;
        return [d.aircraftYear, d.aircraftManufacturer, d.aircraftModel].filter(Boolean).join(' ') || '-';
      },
    },
    {
      headerName: 'Preparer',
      filter: 'agTextColumnFilter',
      width: 140,
      valueGetter: (p) => p.data.preparer?.name || '-',
    },
    {
      headerName: 'Created By',
      filter: 'agTextColumnFilter',
      width: 140,
      valueGetter: (p) => p.data.createdByName || p.data.preparer?.name || '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      filter: 'agTextColumnFilter',
      width: 120,
      valueGetter: (p) => {
        const d = p.data;
        return (d.reportCount > 0) ? 'Published' : (STATUS_LABELS[d.status] || d.status);
      },
      cellRenderer: (p) => {
        const d = p.data;
        const isPublished = d.reportCount > 0;
        return (
          <Chip
            size="sm"
            value={isPublished ? 'Published' : (STATUS_LABELS[d.status] || d.status)}
            color={isPublished ? 'green' : (STATUS_COLORS[d.status] || 'gray')}
            className="text-xs py-0.5 px-2"
          />
        );
      },
    },
    {
      headerName: 'Versions',
      width: 100,
      filter: false,
      cellStyle: { textAlign: 'center' },
      valueGetter: (p) => p.data.reportCount || 0,
      cellRenderer: (p) => {
        const count = p.value;
        if (count > 0) {
          return (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-medium">
              {count}
            </span>
          );
        }
        return <span className="text-gray-300">-</span>;
      },
    },
    {
      field: 'lastFinalizedAt',
      headerName: 'Published',
      width: 140,
      filter: false,
      valueFormatter: (p) => p.value ? friendlyTimeFormatDt(p.value) : '-',
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 140,
      filter: 'agDateColumnFilter',
      valueFormatter: (p) => p.value ? friendlyTimeFormatDt(p.value) : '-',
    },
    ...(isAdmin ? [{
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const draftId = params.data.draftId || params.data.id;
        const hasActiveLinks = (params.data.activeShareCount || 0) > 0;
        return (
          <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
            <Tooltip content="Edit">
              <IconButton variant="text" size="sm" className="h-7 w-7" onClick={() => onEdit(draftId)}>
                <PencilIcon className="h-3.5 w-3.5" />
              </IconButton>
            </Tooltip>
            <Tooltip content="Archive">
              <IconButton
                variant="text"
                size="sm"
                className="h-7 w-7"
                onClick={() => onArchive(draftId, true)}
                disabled={archivingId === draftId}
              >
                {archivingId === draftId ? (
                  <Spinner className="h-3.5 w-3.5" />
                ) : (
                  <ArchiveBoxIcon className="h-3.5 w-3.5" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip content={hasActiveLinks ? "Deactivate share links" : "No active share links"}>
              <span>
                <IconButton
                  variant="text"
                  size="sm"
                  className="h-7 w-7"
                  onClick={() => onDeactivateLinks(draftId)}
                  disabled={deactivatingId === draftId || !hasActiveLinks}
                >
                  {deactivatingId === draftId ? (
                    <Spinner className="h-3.5 w-3.5" />
                  ) : (
                    <NoSymbolIcon className={`h-3.5 w-3.5 ${hasActiveLinks ? 'text-red-400' : 'text-gray-500'}`} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </div>
        );
      },
    }] : []),
  ], [isAdmin, archivingId, onEdit, onArchive, onDeactivateLinks, deactivatingId]);

  if (drafts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <Typography>No reports found</Typography>
      </div>
    );
  }

  const selectedDraftId = selectedDraft ? (selectedDraft.draftId || selectedDraft.id) : null;
  const draftData = selectedDraftId ? expandedData[selectedDraftId] : null;
  const isDetailLoading = selectedDraftId ? expandLoading[selectedDraftId] : false;

  return (
    <>
      <div className="ag-theme-quartz" style={{ height: 'calc(100vh - 250px)' }}>
        <AgGridReact
          rowData={drafts}
          columnDefs={colDefs}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            suppressMovable: true,
          }}
          getRowId={(params) => params.data.draftId || params.data.id}
          domLayout="normal"
          rowHeight={44}
          headerHeight={44}
          pagination={drafts.length > 50}
          paginationPageSize={50}
          onRowClicked={(e) => {
            if (e.event?.defaultPrevented) return;
            handleRowClick(e.data);
          }}
          rowClass="cursor-pointer"
        />
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selectedDraft} handler={() => setSelectedDraft(null)} size="md">
        <DialogHeader className="pb-2">
          <div>
            <Typography variant="h6">
              {selectedDraft?.customerName || 'Untitled'}
            </Typography>
            <Typography variant="small" color="gray">
              {[selectedDraft?.aircraftYear, selectedDraft?.aircraftManufacturer, selectedDraft?.aircraftModel].filter(Boolean).join(' ') || '-'}
              {' · '}
              {selectedDraft?.preparer?.name || '-'}
            </Typography>
          </div>
        </DialogHeader>
        <DialogBody className="pt-0">
          {isDetailLoading ? (
            <div className="flex items-center justify-center py-6">
              <Spinner className="h-5 w-5" />
            </div>
          ) : draftData ? (
            draftData.reports.length === 0 ? (
              <Typography variant="small" className="text-gray-400 italic py-4">No published versions</Typography>
            ) : (
              <div className="space-y-3">
                {draftData.reports.map((report) => {
                  const shares = draftData.sharesById[report.reportId] || [];
                  return (
                    <div key={report.reportId} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
                          v{report.version || '?'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {report.createdAt ? friendlyTimeFormatDt(report.createdAt) : ''}
                        </span>
                        <Tooltip content="Share details">
                          <IconButton
                            variant="text"
                            size="sm"
                            className="p-0.5 h-6 w-6"
                            onClick={() => {
                              setSelectedDraft(null);
                              setShareDialogReport({
                                reportId: report.reportId,
                                title: selectedDraft?.customerName || '',
                                isOwner: selectedDraft?.ownerUid === user?.uid || delegateFor.includes(selectedDraft?.ownerUid),
                              });
                            }}
                          >
                            <ShareIcon className="h-3.5 w-3.5 text-blue-500" />
                          </IconButton>
                        </Tooltip>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {shares.length > 0 ? shares.map((share) => (
                          <span
                            key={share.shareId}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${isShareLive(share) ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                          >
                            {share.customerEmail || share.recipientEmail || share.shareId?.slice(-8) || '-'}
                            <span className={`w-1.5 h-1.5 rounded-full ${isShareLive(share) ? 'bg-green-500' : 'bg-gray-300'}`} />
                          </span>
                        )) : (
                          <span className="text-xs text-gray-400 italic">No shares</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <div className="flex items-center justify-center py-6">
              <Spinner className="h-5 w-5" />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <div className="flex gap-2">
            <Button variant="text" onClick={() => setSelectedDraft(null)}>Close</Button>
            <Button onClick={() => { setSelectedDraft(null); onEdit(selectedDraftId); }}>
              Open in Editor
            </Button>
          </div>
        </DialogFooter>
      </Dialog>
    </>
  );
}

function DraftsListPage() {
  const { user, reportAnalytics, isAdmin, delegateFor } = UserAuth();
  // Match the server gate on getAllDrafts (admin || reportAnalytics).
  const canViewAllReports = isAdmin || reportAnalytics;
  const hasDelegateFor = delegateFor.length > 0;
  const [ownDrafts, setOwnDrafts] = useState([]);
  const [delegatedDrafts, setDelegatedDrafts] = useState([]);
  const [archivedDrafts, setArchivedDrafts] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivedLoaded, setArchivedLoaded] = useState(false);
  const [analyticsMap, setAnalyticsMap] = useState({});
  const [shareDialogReport, setShareDialogReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [archivingId, setArchivingId] = useState(null);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('own');
  const navigate = useNavigate();

  // All Drafts tab — Cloud Function with offset pagination
  // const [allDraftsPage, setAllDraftsPage] = useState(1);
  const [allDraftsCount, setAllDraftsCount] = useState(0);
  const [allDraftsList, setAllDraftsList] = useState([]);
  const [allDraftsLoading, setAllDraftsLoading] = useState(false);
  // const allDraftsPerPage = 25;

  const loadAllDrafts = useCallback(async (page = 1) => {
    setAllDraftsLoading(true);
    try {
      // Load all drafts at once for AG Grid filtering/sorting
      const res = await getAllDrafts({ page: 1, limit: 1000 });
      if (res.success && res.data) {
        setAllDraftsList(res.data.drafts || []);
        setAllDraftsCount(res.data.total || 0);
        // setAllDraftsPage(1);
      }
    } catch (err) {
      console.error("Failed to load all drafts:", err);
    } finally {
      setAllDraftsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'allDrafts' && allDraftsList.length === 0 && !allDraftsLoading) {
      loadAllDrafts(1);
    }
  }, [activeTab, allDraftsList.length, allDraftsLoading, loadAllDrafts]);

  // const handleAllDraftsPageChange = useCallback((nextPage) => {
  //   loadAllDrafts(nextPage);
  // }, [loadAllDrafts]);

  // Expandable shares for All Drafts tab
  const [expandedDrafts, setExpandedDrafts] = useState({});
  const [expandedData, setExpandedData] = useState({});
  const [expandLoading, setExpandLoading] = useState({});

  const toggleExpandDraft = useCallback(async (draftId) => {
    if (expandedDrafts[draftId]) {
      setExpandedDrafts((prev) => ({ ...prev, [draftId]: false }));
      return;
    }
    setExpandedDrafts((prev) => ({ ...prev, [draftId]: true }));

    if (expandedData[draftId]) return; // already fetched

    setExpandLoading((prev) => ({ ...prev, [draftId]: true }));
    try {
      // Query reports directly from Firestore (single query by draftId)
      const reportsSnap = await getDocs(
        query(collection(db, "reports"), where("draftId", "==", draftId))
      );
      const reports = reportsSnap.docs
        .map((doc) => ({ reportId: doc.id, ...doc.data() }))
        .sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""));

      // Batch query shares for all reportIds (Firestore 'in' supports up to 30)
      const sharesById = {};
      const reportIds = reports.map((r) => r.reportId);
      if (reportIds.length > 0) {
        // Split into chunks of 30 for Firestore 'in' limit
        for (let i = 0; i < reportIds.length; i += 30) {
          const chunk = reportIds.slice(i, i + 30);
          const sharesSnap = await getDocs(
            query(collection(db, "report_shares"), where("reportId", "in", chunk))
          );
          sharesSnap.docs.forEach((doc) => {
            const data = { shareId: doc.id, ...doc.data() };
            if (!sharesById[data.reportId]) sharesById[data.reportId] = [];
            sharesById[data.reportId].push(data);
          });
        }
        // Sort shares by createdAt desc within each report
        Object.values(sharesById).forEach((arr) =>
          arr.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))
        );
      }

      setExpandedData((prev) => ({ ...prev, [draftId]: { reports, sharesById } }));
    } catch (err) {
      console.error("Failed to load shares for draft:", draftId, err);
    } finally {
      setExpandLoading((prev) => ({ ...prev, [draftId]: false }));
    }
  }, [expandedDrafts, expandedData]);

  const loadDrafts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch drafts and analytics in parallel
      const [draftsData, analyticsData] = await Promise.all([
        listDrafts({ includeDelegated: hasDelegateFor }),
        getMyReportsAnalytics().catch(() => ({ reports: [] })),
      ]);

      // Handle { own: [...], team: [...], delegated: [...] } response format
      if (draftsData && typeof draftsData === 'object') {
        setOwnDrafts(Array.isArray(draftsData.own) ? draftsData.own : []);
        setDelegatedDrafts(Array.isArray(draftsData.delegated) ? draftsData.delegated : []);
      } else if (Array.isArray(draftsData)) {
        setOwnDrafts(draftsData);
      } else {
        console.warn('Unexpected drafts response format:', draftsData);
        setOwnDrafts([]);
      }

      // Build analytics lookup map by draftId
      if (analyticsData?.reports) {
        const aMap = {};
        for (const report of analyticsData.reports) {
          if (report.draftId) {
            // Aggregate analytics per draft (a draft can have multiple report versions)
            if (!aMap[report.draftId]) {
              aMap[report.draftId] = {
                totalReportOpened: 0,
                totalEngagedViews: 0,
                uniqueViewers: 0,
                totalActiveSeconds: 0,
                reportCount: 0,
                activeShareCount: 0,
                lastPublishedAt: null,
              };
            }
            aMap[report.draftId].totalReportOpened += report.totalReportOpened || 0;
            aMap[report.draftId].totalEngagedViews += report.totalEngagedViews || 0;
            aMap[report.draftId].uniqueViewers += report.uniqueViewers || 0;
            aMap[report.draftId].totalActiveSeconds += report.totalActiveSeconds || 0;
            aMap[report.draftId].reportCount += 1;
            aMap[report.draftId].activeShareCount += report.activeShareCount || 0;
            // Track most recent publish date
            if (report.createdAt) {
              const reportDate = new Date(report.createdAt);
              if (!aMap[report.draftId].lastPublishedAt || reportDate > new Date(aMap[report.draftId].lastPublishedAt)) {
                aMap[report.draftId].lastPublishedAt = report.createdAt;
              }
            }
          }
        }
        setAnalyticsMap(aMap);
      }
    } catch (err) {
      console.error('Failed to load drafts:', err);
      setError(err.message);
      setOwnDrafts([]);
    } finally {
      setIsLoading(false);
    }
  }, [hasDelegateFor]);

  const loadArchivedDrafts = useCallback(async () => {
    if (archivedLoaded || archivedLoading) return;
    try {
      setArchivedLoading(true);
      const data = await listDrafts({ includeArchived: true });
      if (data && typeof data === 'object') {
        const ownAll = Array.isArray(data.own) ? data.own : [];
        const teamAll = Array.isArray(data.team) ? data.team : [];
        setArchivedDrafts([
          ...ownAll.filter(d => d.archived),
          ...teamAll.filter(d => d.archived),
        ]);
      }
      setArchivedLoaded(true);
    } catch (err) {
      console.error('Failed to load archived drafts:', err);
    } finally {
      setArchivedLoading(false);
    }
  }, [archivedLoaded, archivedLoading]);

  useEffect(() => {
    if (user?.uid) {
      loadDrafts();
    }
  }, [user?.uid, loadDrafts]);

  // Lazy-load archived drafts when tab is selected
  useEffect(() => {
    if (activeTab === 'archived' && !archivedLoaded) {
      loadArchivedDrafts();
    }
  }, [activeTab, archivedLoaded, loadArchivedDrafts]);

  const handleEditDraft = (draftId) => {
    navigate(`/dashboard/report-editor/${draftId}`);
  };

  const handleDeactivateLinks = async (draftId) => {
    if (!window.confirm('Deactivate all active share links for this report? Anyone with a link will lose access. You can create a new share link afterwards.')) return;
    try {
      setDeactivatingId(draftId);
      setError(null);
      const result = await deactivateDraftShares(draftId);
      setNotice(result.deactivated > 0
        ? `Deactivated ${result.deactivated} share link${result.deactivated === 1 ? '' : 's'}.`
        : 'No active share links found for this report.');
      // Drop cached share details so the expanded view reloads fresh
      setExpandedData((prev) => {
        const next = { ...prev };
        delete next[draftId];
        return next;
      });
      // Zero the Active Links count immediately for visible feedback
      setAnalyticsMap((prev) => prev[draftId]
        ? { ...prev, [draftId]: { ...prev[draftId], activeShareCount: 0 } }
        : prev);
    } catch (err) {
      console.error('Failed to deactivate share links:', err);
      setError(err.message);
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleArchive = async (draftId, shouldArchive) => {
    try {
      setArchivingId(draftId);
      await archiveDraft(draftId, shouldArchive);
      // Reload active drafts and invalidate archived cache
      setArchivedLoaded(false);
      setArchivedDrafts([]);
      await loadDrafts();
      // If currently on archived tab, re-fetch immediately
      if (activeTab === 'archived') {
        loadArchivedDrafts();
      }
    } catch (err) {
      console.error('Failed to archive/unarchive:', err);
      setError(err.message);
    } finally {
      setArchivingId(null);
    }
  };

  const DraftsTable = ({ drafts, showPreparer = false, showCreatedBy = false, analyticsMap = {}, isArchived = false, showActions = true, showAnalytics = true, showExpand = false }) => {
    if (drafts.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <Typography>No reports found</Typography>
          {!showPreparer && (
            <Button
              variant="text"
              className="mt-2"
              onClick={() => navigate('/dashboard/report-editor')}
            >
              Create your first draft
            </Button>
          )}
        </div>
      );
    }

    return (
      <div>
        <div className="overflow-x-auto">
          <table className="w-full table-auto text-left text-sm">
            <thead>
              <tr className="bg-gray-50/80">
                {showExpand && (
                  <th className="border-b border-gray-200 px-1 py-2.5 w-8" />
                )}
                <th className="border-b border-gray-200 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-700 whitespace-nowrap">
                  Aircraft
                </th>
                <th className="border-b border-gray-200 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-700 whitespace-nowrap">
                  Customer
                </th>
                {(showPreparer || showCreatedBy) && (
                  <th className="border-b border-gray-200 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-700 whitespace-nowrap">
                    Preparer
                  </th>
                )}
                <th className="border-b border-gray-200 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-700 whitespace-nowrap">
                  Status
                </th>
                <th className="border-b border-gray-200 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-700 whitespace-nowrap text-center">
                  Links
                </th>
                {showAnalytics && (
                  <th className="border-b border-gray-200 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-700 whitespace-nowrap text-center">
                    Engagement
                  </th>
                )}
                <th className="border-b border-gray-200 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-700 whitespace-nowrap">
                  Published / Created
                </th>
                {showActions && (
                  <th className="border-b border-gray-200 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-700 whitespace-nowrap text-center">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
            {drafts.map((draft, index) => {
              const isLast = index === drafts.length - 1;
              const cellClass = `px-3 py-2 ${isLast && !(showExpand && expandedDrafts[draft.draftId || draft.id]) ? '' : 'border-b border-gray-100'}`;
              const draftId = draft.draftId || draft.id;
              const analytics = analyticsMap[draftId] || {};
              const isExpanded = showExpand && expandedDrafts[draftId];
              const draftData = expandedData[draftId];
              const isExpandLoading = expandLoading[draftId];
              // Count total columns for colspan
              const colCount = 2 + (showPreparer || showCreatedBy ? 1 : 0) + 1 + 1 + (showAnalytics ? 1 : 0) + 1 + (showActions ? 1 : 0) + (showExpand ? 1 : 0);

              return (
                <React.Fragment key={draftId}>
                <tr
                  className="hover:bg-blue-50/50 cursor-pointer transition-colors"
                  onClick={() => showExpand ? toggleExpandDraft(draftId) : handleEditDraft(draftId)}
                >
                  {showExpand && (
                    <td className={`px-1 py-2 ${isLast && !isExpanded ? '' : 'border-b border-gray-100'}`} onClick={(e) => { e.stopPropagation(); toggleExpandDraft(draftId); }}>
                      {isExpandLoading ? (
                        <Spinner className="h-3.5 w-3.5 mx-auto" />
                      ) : (
                        <ChevronDownIcon className={`h-4 w-4 mx-auto text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      )}
                    </td>
                  )}
                  <td className={cellClass}>
                    {(() => {
                      const isMarketing = draft.reportType === 'marketing_update';
                      const reg = draft.aircraftId || draft.aircraftRegistration || draft.subjectAircraft?.registration || '';
                      const title = [draft.aircraftYear, draft.aircraftManufacturer, draft.aircraftModel].filter(Boolean).join(' ');
                      return (
                        <div className="leading-tight py-0.5">
                          <div className="font-semibold text-gray-900 whitespace-nowrap">{title || reg || '-'}</div>
                          <div className="flex items-center gap-1.5 mt-1 whitespace-nowrap">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${isMarketing ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                              {isMarketing ? 'Marketing' : 'Valuation'}
                            </span>
                            {reg && (
                              <span className="font-mono text-xs text-gray-700">{reg}</span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td className={cellClass}>
                    <span className="font-medium text-gray-800 whitespace-nowrap">
                      {draft.customerName || '-'}
                    </span>
                  </td>
                  {(showPreparer || showCreatedBy) && (
                    <td className={cellClass}>
                      {(() => {
                        const preparerName = draft.preparer?.name || '-';
                        const createdByName = draft.createdByName;
                        const differs = createdByName && createdByName !== draft.preparer?.name;
                        return (
                          <div className="leading-tight whitespace-nowrap">
                            <span className="text-gray-800">{preparerName}</span>
                            {differs && (
                              <div className="text-xs text-gray-600 mt-0.5">by {createdByName}</div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  )}
                  <td className={cellClass}>
                    {(() => {
                      // Consider published if has reportCount OR has analytics (meaning reports exist)
                      const hasPublishedVersions = draft.reportCount > 0 || analytics.totalReportOpened !== undefined;
                      const versionCount = draft.reportCount || analytics.reportCount || 0;
                      return (
                        <div className="flex items-center gap-1.5 whitespace-nowrap">
                          <Chip
                            size="sm"
                            value={hasPublishedVersions ? 'Published' : (STATUS_LABELS[draft.status] || draft.status)}
                            color={hasPublishedVersions ? 'green' : (STATUS_COLORS[draft.status] || 'gray')}
                            className="text-xs py-0.5 px-2"
                          />
                          {versionCount > 1 && (
                            <Tooltip content={`${versionCount} published versions`}>
                              <span className="text-xs font-semibold text-gray-700">v{versionCount}</span>
                            </Tooltip>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className={`${cellClass} text-center`}>
                    {analytics.activeShareCount > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        {analytics.activeShareCount}
                      </span>
                    ) : analytics.activeShareCount === 0 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-medium">0</span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  {showAnalytics && (
                    <td className={`${cellClass} text-center`}>
                      {analytics.totalReportOpened !== undefined ? (
                        <div className="inline-flex items-center gap-2.5 text-xs tabular-nums whitespace-nowrap">
                          <Tooltip content="Report opens">
                            <span className={`flex items-center gap-1 ${analytics.totalReportOpened > 0 ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                              <EyeIcon className="h-3.5 w-3.5 opacity-70" />
                              {analytics.totalReportOpened || 0}
                            </span>
                          </Tooltip>
                          <Tooltip content="Engaged views">
                            <span className={`flex items-center gap-1 ${analytics.totalEngagedViews > 0 ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                              <ListBulletIcon className="h-3.5 w-3.5 opacity-70" />
                              {analytics.totalEngagedViews || 0}
                            </span>
                          </Tooltip>
                          <Tooltip content="Verified viewers">
                            <span className={`flex items-center gap-1 ${analytics.uniqueViewers > 0 ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                              <CheckBadgeIcon className="h-3.5 w-3.5 opacity-70" />
                              {analytics.uniqueViewers || 0}
                            </span>
                          </Tooltip>
                          <Tooltip content="Active reading time">
                            <span className={`flex items-center gap-1 font-medium ${analytics.totalActiveSeconds > 0 ? getEngagementColor(analytics.totalActiveSeconds) : 'text-gray-400'}`}>
                              <ClockIcon className="h-3.5 w-3.5 opacity-70" />
                              {formatActiveTime(analytics.totalActiveSeconds) || '0s'}
                            </span>
                          </Tooltip>
                        </div>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                  )}
                  <td className={cellClass}>
                    {(() => {
                      const publishedAt = draft.lastFinalizedAt || analytics.lastPublishedAt;
                      return (
                        <div className="leading-tight whitespace-nowrap">
                          <div className="text-xs font-medium text-gray-800">
                            {publishedAt ? friendlyTimeFormatDt(publishedAt) : <span className="font-normal text-gray-400">Not published</span>}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            Created {draft.createdAt ? friendlyTimeFormatDt(draft.createdAt) : '-'}
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  {showActions && (
                    <td className={`${cellClass} text-center`} onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-0.5">
                        <Tooltip content="Edit">
                          <IconButton
                            variant="text"
                            size="sm"
                            className="h-7 w-7"
                            onClick={() => handleEditDraft(draftId)}
                          >
                            <PencilIcon className="h-3.5 w-3.5" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip content={isArchived ? "Unarchive" : "Archive"}>
                          <IconButton
                            variant="text"
                            size="sm"
                            className="h-7 w-7"
                            onClick={() => handleArchive(draftId, !isArchived)}
                            disabled={archivingId === draftId}
                          >
                            {archivingId === draftId ? (
                              <Spinner className="h-3.5 w-3.5" />
                            ) : isArchived ? (
                              <ArchiveBoxXMarkIcon className="h-3.5 w-3.5" />
                            ) : (
                              <ArchiveBoxIcon className="h-3.5 w-3.5" />
                            )}
                          </IconButton>
                        </Tooltip>
                        {(() => {
                          const hasActiveLinks = analytics.activeShareCount > 0;
                          return (
                            <Tooltip content={hasActiveLinks ? "Deactivate share links" : "No active share links"}>
                              <span>
                                <IconButton
                                  variant="text"
                                  size="sm"
                                  className="h-7 w-7"
                                  onClick={() => handleDeactivateLinks(draftId)}
                                  disabled={deactivatingId === draftId || !hasActiveLinks}
                                >
                                  {deactivatingId === draftId ? (
                                    <Spinner className="h-3.5 w-3.5" />
                                  ) : (
                                    <NoSymbolIcon className={`h-3.5 w-3.5 ${hasActiveLinks ? 'text-red-400' : 'text-gray-500'}`} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        })()}
                      </div>
                    </td>
                  )}
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={colCount} className={`px-0 py-0 ${isLast ? '' : 'border-b border-gray-200'} bg-gray-50/50`}>
                      {isExpandLoading ? (
                        <div className="flex items-center justify-center py-3">
                          <Spinner className="h-4 w-4" />
                        </div>
                      ) : draftData ? (
                        <div className="px-4 py-2">
                          {draftData.reports.length === 0 ? (
                            <span className="text-xs text-gray-400 italic">No published versions</span>
                          ) : (
                            <div className="space-y-1.5">
                              {draftData.reports.map((report) => {
                                const shares = draftData.sharesById[report.reportId] || [];
                                return (
                                  <div key={report.reportId} className="flex items-start gap-3 text-xs">
                                    <div className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700">
                                        v{report.version || '?'}
                                      </span>
                                      <span className="text-gray-400">
                                        {report.createdAt ? friendlyTimeFormatDt(report.createdAt) : ''}
                                      </span>
                                      <Tooltip content="Share details">
                                        <IconButton
                                          variant="text"
                                          size="sm"
                                          className="p-0.5 h-5 w-5"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShareDialogReport({
                                              reportId: report.reportId,
                                              title: draft.customerName || '',
                                              isOwner: draft.ownerUid === user?.uid || delegateFor.includes(draft.ownerUid),
                                            });
                                          }}
                                        >
                                          <ShareIcon className="h-3 w-3 text-blue-500" />
                                        </IconButton>
                                      </Tooltip>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                      {shares.length > 0 ? shares.map((share) => (
                                        <span
                                          key={share.shareId}
                                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${isShareLive(share) ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                                          title={`ID: ${share.shareId?.slice(-8) || '-'}\nCreated: ${share.createdAt ? new Date(share.createdAt).toLocaleString() : '-'}`}
                                        >
                                          {share.customerEmail || share.recipientEmail || share.shareId?.slice(-8) || '-'}
                                          <span className={`w-1.5 h-1.5 rounded-full ${isShareLive(share) ? 'bg-green-500' : 'bg-gray-300'}`} />
                                        </span>
                                      )) : (
                                        <span className="text-gray-400 italic">No shares</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <Typography variant="h4" color="blue-gray">
          Reports
        </Typography>
        <div className="flex gap-2">
          <Tooltip content="Refresh">
            <IconButton variant="outlined" onClick={() => {
              loadDrafts();
              if (activeTab === 'allDrafts') {
                setExpandedDrafts({});
                setExpandedData({});
                loadAllDrafts(1);
              }
            }} disabled={isLoading}>
              <ArrowPathIcon className="h-4 w-4" />
            </IconButton>
          </Tooltip>
          <Button
            className="flex items-center gap-2"
            onClick={() => navigate('/dashboard/report-editor')}
          >
            <DocumentPlusIcon className="h-5 w-5" />
            New Report
          </Button>
        </div>
      </div>

      {error && (
        <Alert color="red" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {notice && (
        <Alert color="green" className="mb-4" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner className="h-8 w-8" />
            </div>
          ) : (
            <Tabs value={activeTab}>
              <TabsHeader
                className="bg-transparent border-b border-gray-200 rounded-none p-0 mx-4"
                indicatorProps={{
                  className: "bg-transparent border-b-2 border-blue-500 shadow-none rounded-none",
                }}
              >
                <Tab
                  value="own"
                  onClick={() => setActiveTab('own')}
                  className={`py-4 px-6 ${activeTab === 'own' ? 'text-blue-600' : 'text-gray-600'}`}
                >
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-5 w-5" />
                    <span className="font-medium">My Reports</span>
                    {ownDrafts.length > 0 && (
                      <Chip
                        size="sm"
                        value={ownDrafts.length}
                        color={activeTab === 'own' ? 'blue' : 'gray'}
                        className="rounded-full"
                      />
                    )}
                  </div>
                </Tab>
                {/* Team Reports tab hidden for now */}
                {hasDelegateFor && (
                  <Tab
                    value="delegated"
                    onClick={() => setActiveTab('delegated')}
                    className={`py-4 px-6 ${activeTab === 'delegated' ? 'text-blue-600' : 'text-gray-600'}`}
                  >
                    <div className="flex items-center gap-2">
                      <UserGroupIcon className="h-5 w-5" />
                      <span className="font-medium">Delegated</span>
                      {delegatedDrafts.length > 0 && (
                        <Chip
                          size="sm"
                          value={delegatedDrafts.length}
                          color={activeTab === 'delegated' ? 'blue' : 'gray'}
                          className="rounded-full"
                        />
                      )}
                    </div>
                  </Tab>
                )}
                <Tab
                  value="archived"
                  onClick={() => setActiveTab('archived')}
                  className={`py-4 px-6 ${activeTab === 'archived' ? 'text-blue-600' : 'text-gray-600'}`}
                >
                  <div className="flex items-center gap-2">
                    <ArchiveBoxIcon className="h-5 w-5" />
                    <span className="font-medium">Archived</span>
                    {archivedDrafts.length > 0 && (
                      <Chip
                        size="sm"
                        value={archivedDrafts.length}
                        color={activeTab === 'archived' ? 'blue' : 'gray'}
                        className="rounded-full"
                      />
                    )}
                  </div>
                </Tab>
                {canViewAllReports && (
                  <Tab
                    value="allDrafts"
                    onClick={() => setActiveTab('allDrafts')}
                    className={`py-4 px-6 ${activeTab === 'allDrafts' ? 'text-blue-600' : 'text-gray-600'}`}
                  >
                    <div className="flex items-center gap-2">
                      <GlobeAltIcon className="h-5 w-5" />
                      <span className="font-medium">All Reports</span>
                      {allDraftsCount > 0 && (
                        <Chip
                          size="sm"
                          value={allDraftsCount}
                          color={activeTab === 'allDrafts' ? 'blue' : 'gray'}
                          className="rounded-full"
                        />
                      )}
                    </div>
                  </Tab>
                )}
                {/* All Reports tab removed */}
              </TabsHeader>
              <TabsBody>
                <TabPanel value="own" className="p-0">
                  <DraftsTable drafts={ownDrafts} showPreparer={true} showCreatedBy={true} analyticsMap={analyticsMap} />
                </TabPanel>
                {/* Team Reports panel hidden for now */}
                {hasDelegateFor && (
                  <TabPanel value="delegated" className="p-0">
                    <DraftsTable drafts={delegatedDrafts} showPreparer={true} showCreatedBy={true} analyticsMap={{}} />
                  </TabPanel>
                )}
                <TabPanel value="archived" className="p-0">
                  {archivedLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Spinner className="h-8 w-8" />
                    </div>
                  ) : (
                    <DraftsTable drafts={archivedDrafts} showPreparer={true} showCreatedBy={true} analyticsMap={analyticsMap} isArchived={true} />
                  )}
                </TabPanel>
                <TabPanel value="allDrafts" className="p-0">
                  {allDraftsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Spinner className="h-8 w-8" />
                    </div>
                  ) : (
                    <AllDraftsGrid
                      drafts={allDraftsList}
                      isAdmin={isAdmin}
                      onEdit={handleEditDraft}
                      onArchive={handleArchive}
                      archivingId={archivingId}
                      onDeactivateLinks={handleDeactivateLinks}
                      deactivatingId={deactivatingId}
                      expandedData={expandedData}
                      expandLoading={expandLoading}
                      toggleExpandDraft={toggleExpandDraft}
                      setShareDialogReport={setShareDialogReport}
                      user={user}
                      delegateFor={delegateFor}
                    />
                  )}
                </TabPanel>
                {/* All Reports panel removed */}
              </TabsBody>
            </Tabs>
          )}
        </CardBody>
      </Card>

      <ShareDialog
        open={!!shareDialogReport}
        onClose={() => setShareDialogReport(null)}
        reportId={shareDialogReport?.reportId}
        reportTitle={shareDialogReport?.title || ''}
        reportType={shareDialogReport?.reportType}
        readOnly={shareDialogReport ? !shareDialogReport.isOwner : false}
      />
    </div>
  );
}

export default DraftsListPage;
